import React, { useContext, useEffect, useState } from 'react'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import { PageData } from '@/api/Pagination/Types'
import {
  RewardsHubCreateDto,
  RewardsHubDto,
  RewardsHubTab,
  RewardStatuses,
} from '@/api/Trading-Accounts/Types/RewardsHubDto'
import AddButton from '@/components/AddButton/AddButton'
import ClientAreaAuditLogModal from '@/components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { Pager } from '@/components/components/Pager'
import { FilterType } from '@/components/Filter'
import { SelectFiltersFacade } from '@/components/Filter/ClientFields/SelectFiltersFacade'
import { FilterCell } from '@/components/Filter/FilterCell'
import { Loading } from '@/components/Loading'
import MoreButton from '@/components/MoreButton/MoreButton'
import { successToast, ToastContext } from '@/components/Toast/Toast'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { AddEditRewardTypeDialog } from '@/routes/Marketing/RewardsHub/AddNewRewardModal'
import { rewardsHubColumns } from '@/routes/Marketing/RewardsHub/RewardsHubColumns'
import { rewardsHubFilteringFields } from '@/routes/Marketing/RewardsHub/rewardsHubFilteringFields'
import { RewardsHubNavigation } from '@/routes/Marketing/RewardsHub/RewardsHubNavigation'
import { StatusConfirmationModal } from '@/routes/Marketing/RewardsHub/StatusConfirmationModal'
import { useRewardFiltering } from '@/routes/Marketing/RewardsHub/useRewardFilteringNew'
import { GridFilterActionState } from '@/store/grid'
import { useApiClient } from '@/utils/api/ApiClient'
import { TradingAccountsApiClient } from '@/utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from '@/utils/api/useCallbackWithForceRefresh'
import { useFetchOneWithFetchingUpdate } from '@/utils/api/useFetchOneWithFetchingUpdate'
import { AuthSessionContext } from '@/utils/AuthContext'
import { links } from '@/utils/featureToggles'
import { hasPermission } from '@/utils/permissionsValidator'
import { TableStateContext } from '@/utils/TableStateContext'
import { GridColumn as Column, Grid, GridCellProps, GridColumn } from '@progress/kendo-react-grid'

export const RewardsTypeManager: React.FC = () => {
  const [auth] = useContext(AuthSessionContext)
  const [auditLogModal, setAuditLogModal] = useState(false)

  const {
    tableData,
    setTableData,
    data,
    setData,
    getSort,
    gridState,
    handleSkipCountChange,
    handleSortChange,
    handleGridPageChange,
    handlePageSizeChange,
    gridStatusState,
    gridStatusActions,
    gridActions,
    filterFetch,
    getQuery,
    fetchGrid,
  } = useRewardFiltering()

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const { callback: rewardsCallback } = useCallbackWithForceRefresh(() => {
    return tradingAccountsApiClient.getRewards()
  }, [])
  const { isLoading } = useFetchOneWithFetchingUpdate(rewardsCallback)

  const permissions = auth?.permissions || []
  const setToast = useContext(ToastContext)
  const [showAddEditRewardType, setAddEditRewardType] = useState<{
    entity?: RewardsHubDto
    visible: boolean
  }>({
    visible: false,
  })

  const [openStatusModal, setOpenStatusModal] = useState<{
    entity?: RewardsHubDto
    visible: boolean
  }>({ visible: false })

  const mapToCreateDto = (entity: RewardsHubDto): RewardsHubCreateDto => ({
    internalName: entity.internalName,
    displayName: entity.displayName,
    rewardCategoryId: entity.rewardCategory.id,
    rewardStatusId: entity.rewardStatus.id,
    defaultExpirationInDays: entity.defaultExpirationInDays,
    spinRewardConfiguration: entity.spinRewardConfiguration
      ? {
          amountOfSpins: entity.spinRewardConfiguration.amountOfSpins,
          luckyDrawCampaignId: entity.spinRewardConfiguration.luckyDrawCampaign.id,
        }
      : null,
    bonusRewardConfiguration: entity.bonusRewardConfiguration
      ? { bonusCampaignId: entity.bonusRewardConfiguration.bonusCampaign.id }
      : null,
    tradingViewRewardConfiguration: entity.tradingViewRewardConfiguration
      ? { tradingViewPlanId: entity.tradingViewRewardConfiguration.tradingViewPlan.id.toString() }
      : null,
  })

  const loadGrid = async () => {
    await fetchGrid(
      async (gridFilterState: GridFilterActionState): Promise<PageData<RewardsHubDto[]>> => {
        const searchQuery = getQuery(gridFilterState.filter)
        const sortQuery = getSort(gridFilterState.sort)
        const response = await tradingAccountsApiClient.getRewards({
          search: searchQuery,
          sort: sortQuery,
          pageIndex: gridFilterState.pageIndex,
          pageSize: gridFilterState.pageSize,
          skipCount: tableData?.skipCount !== false,
        })

        if (response?.items) setData(response.items)
        setTableData({ ...tableData, meta: response })
        return response
      }
    )
  }

  useEffect(() => {
    loadGrid()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleStatus = async (entity: RewardsHubDto) => {
    const newStatusId =
      entity.rewardStatus.id === RewardStatuses.ACTIVE
        ? RewardStatuses.DISABLED
        : RewardStatuses.ACTIVE

    await tradingAccountsApiClient.updateReward(entity.id, {
      ...mapToCreateDto(entity),
      rewardStatusId: newStatusId,
    })

    setToast(
      successToast(
        `Reward Type ${newStatusId === RewardStatuses.ACTIVE ? 'activated' : 'deactivated'}.`
      )
    )
    await loadGrid()
  }

  const onSubmit = async (values: RewardsHubCreateDto) => {
    if (showAddEditRewardType.entity) {
      await tradingAccountsApiClient.updateReward(showAddEditRewardType.entity.id, values)
      setToast(successToast('Reward Type updated.'))
    } else {
      await tradingAccountsApiClient.addReward(values)
      setToast(successToast('Reward Type added.'))
    }

    setAddEditRewardType({ entity: undefined, visible: false })
    await loadGrid()
  }

  return (
    <>
      {auditLogModal && (
        <ClientAreaAuditLogModal
          isVisible={auditLogModal}
          onClose={() => setAuditLogModal(false)}
          rewardId
        />
      )}
      <RewardsHubNavigation tab={RewardsHubTab.MANAGER} />
      <AddEditRewardTypeDialog
        key={showAddEditRewardType.visible ? (showAddEditRewardType.entity?.id ?? 'new') : 'closed'}
        isOpen={showAddEditRewardType.visible}
        onClose={() => setAddEditRewardType({ visible: false, entity: undefined })}
        entity={showAddEditRewardType.entity}
        onSubmit={onSubmit}
      />
      <StatusConfirmationModal
        isOpen={openStatusModal.visible}
        entity={openStatusModal.entity}
        onConfirm={handleToggleStatus}
        onClose={() => setOpenStatusModal({ visible: false, entity: undefined })}
      />
      <Breadcrumbs
        crumbs={[{ title: 'Rewards Type Manager', link: links.rewardTypeManager.url }]}
      />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.MARKETING_CAMPAIGNS_OVERVIEW}
        filterFields={rewardsHubFilteringFields()}
        getQuery={getQuery}
        extraHeaderActions={<Button onClick={() => setAuditLogModal(true)}>Audit Log</Button>}
      />
      <Loading isLoading={isLoading}>
        <AddButton
          text='Create New Reward Type'
          disabled={!hasPermission(permissions, PermissionsEnum.CanViewRewards)}
          onClick={() => {
            setAddEditRewardType({ visible: true, entity: undefined })
          }}
          data-test='create-new-reward-type'
        />
        <TableStateContext.Provider value={[tableData, setTableData]}>
          <Grid
            {...gridState}
            resizable
            data={data}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            pageable
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {rewardsHubColumns
              .filter((c) => c.show)
              .map((column, idx) => (
                <GridColumn key={idx} {...column} />
              ))}
            <Column
              width={50}
              title=''
              filterCell={FilterCell}
              cell={(gridCellProps: GridCellProps) => (
                <td>
                  <MoreButton
                    items={[
                      {
                        name: 'Edit',
                        'data-test': 'edit',
                        disabled: !hasPermission(
                          permissions,
                          PermissionsEnum.CanEditComplianceCountryRisk
                        ),
                        onClick: () => {
                          setAddEditRewardType({ visible: true, entity: gridCellProps.dataItem })
                        },
                      },
                      {
                        name:
                          gridCellProps.dataItem.rewardStatus.id === RewardStatuses.DISABLED
                            ? 'Activate'
                            : 'Disable',
                        'data-test': 'toggle-status',
                        onClick: () =>
                          setOpenStatusModal({ visible: true, entity: gridCellProps.dataItem }),
                      },
                    ]}
                    data-test='more-button'
                  />
                </td>
              )}
            />
          </Grid>
        </TableStateContext.Provider>
      </Loading>
    </>
  )
}
